/**
 * Skeleton — the placeholder for "the layout is known, the data is not yet here".
 *
 * CLAUDE.md, "Loading states", first case, and the one that covers almost every partial load:
 * a pane refetching, a drawer opening on an id, a tab switching, pagination, a filter change.
 * If the screen can promise a shape, it draws the shape. The page loader is for the other
 * situation — nothing known yet — and the two are not interchangeable.
 *
 * **Row counts are a guess about content, so they stay modest.** Six shadows followed by "no
 * projects" is a lie the empty state then has to walk back, which is why `rows` defaults to
 * five and is capped at six rather than being whatever the caller's page size happens to be.
 *
 * **The bars are `aria-hidden`; the container is the announcement.** A screen reader that read
 * out nine placeholder rectangles would be reading the scaffolding. `SkeletonRows` and
 * `SkeletonLines` therefore take a `label` and carry `role="status"` with `aria-busy` — the
 * same sentence a sighted person would have seen, said once.
 */

import type { CSSProperties } from 'react';

import styles from './Skeleton.module.css';

/** What `rows` is allowed to reach, and what it defaults to. See the module note. */
const MAX_ROWS = 6;
const DEFAULT_ROWS = 5;

/** A bar standing in for one line of 13px text. */
const TEXT_BAR = 12;

/**
 * The detail pane's ragged right. Cycled rather than random: a width that changes between two
 * renders of the same pending pane is a placeholder that flickers while nothing has happened.
 */
const LINE_WIDTHS = ['100%', '82%', '94%', '68%', '88%', '76%'];

/** Numbers are pixels, strings are whatever the caller wrote (`%`, `ch`, `var(--…)`). */
type Size = number | string;

function px(value: Size): string {
  return typeof value === 'number' ? `${String(value)}px` : value;
}

export interface SkeletonProps {
  readonly width: Size;
  readonly height: Size;
}

export function Skeleton({ width, height }: SkeletonProps) {
  return <span className={styles.bar} style={{ width: px(width), height: px(height) }} aria-hidden />;
}

export interface SkeletonRowsProps {
  /** What a screen reader hears instead of the bars — the screen's own loading sentence. */
  readonly label: string;
  /** Capped at six, floored at one. Five if not given. */
  readonly rows?: number;
  /** One bar per entry, in row order; numbers are pixels, strings pass through. */
  readonly columns: readonly Size[];
  /**
   * 40px by default, which suits a card or list row. A stand-in for `TreeTable` should pass
   * `36` — `--tree-row-height` — or the real rows will move up under the placeholder as they
   * land, which is the jump the rule exists to prevent.
   */
  readonly rowHeight?: number;
}

export function SkeletonRows({ label, rows = DEFAULT_ROWS, columns, rowHeight = 40 }: SkeletonRowsProps) {
  const drawn = Math.max(1, Math.min(rows, MAX_ROWS));
  const style: CSSProperties = { height: `${String(rowHeight)}px` };

  return (
    <div className={styles.rows} role="status" aria-busy="true" aria-label={label}>
      {Array.from({ length: drawn }, (_, row) => (
        <div key={row} className={styles.row} style={style}>
          {columns.map((width, column) => (
            <Skeleton key={column} width={width} height={TEXT_BAR} />
          ))}
        </div>
      ))}
    </div>
  );
}

export interface SkeletonLinesProps {
  /** What a screen reader hears instead of the bars — the pane's own loading sentence. */
  readonly label: string;
  /** Capped at six, floored at one. Four if not given — a detail pane is not a list. */
  readonly lines?: number;
}

export function SkeletonLines({ label, lines = 4 }: SkeletonLinesProps) {
  const drawn = Math.max(1, Math.min(lines, MAX_ROWS));

  return (
    <div className={styles.lines} role="status" aria-busy="true" aria-label={label}>
      {Array.from({ length: drawn }, (_, index) => (
        <Skeleton key={index} width={LINE_WIDTHS[index % LINE_WIDTHS.length] ?? '100%'} height={TEXT_BAR} />
      ))}
    </div>
  );
}
